import React, { useState, useEffect } from 'react';
import { CurrencySelect } from '../components/CurrencySelect';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Edit2, Archive, ArchiveRestore, Search, Truck, Package, AlertTriangle, Mail, Phone, Clock, DollarSign, Eye, ShieldAlert, Printer, Star } from 'lucide-react';
import { openLetterheadWindow } from '../utils/brandedPrint';
import { toast } from 'sonner';
import EmailPurchaserDialog from '../components/EmailPurchaserDialog';
import { useNavigate } from 'react-router-dom';

const VendorsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [vendorCardOpen, setVendorCardOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorItems, setVendorItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [purchaserDialogOpen, setPurchaserDialogOpen] = useState(false);
  const [purchaserContext, setPurchaserContext] = useState({ type: 'vendor_order', item: null, vendor: null });
  const [formData, setFormData] = useState({
    vendor_name: '',
    contact_name: '',
    contact_info: '',
    contact_email: '',
    contact_phone: '',
    website: '',
    payment_terms: '',
    gst_number: '',
    default_currency: 'CAD',
    min_order_value: '',
    ordering_notes: '',
    preferred_order_process: '',
    lead_time_days: 0,
    active_status: true,
    star_rating: null,
    delay_risk: 1,
    cost_risk: 1,
    geopolitical_risk: 1,
    risk_notes: '',
  });
  const [productSummary, setProductSummary] = useState(null);
  const [productSummaryLoading, setProductSummaryLoading] = useState(false);

  const fetchData = async () => {
    try {
      const [vendorsRes, itemsRes] = await Promise.all([
        api().get(`/vendors${showArchived ? '?include_archived=true' : ''}`),
        api().get('/items')
      ]);
      setVendors(vendorsRes.data);
      setItems(itemsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [showArchived]);

  const openVendorCard = (vendor) => {
    setSelectedVendor(vendor);
    const suppliedItems = items.filter(item => item.preferred_vendor_id === vendor.id);
    setVendorItems(suppliedItems);
    setVendorCardOpen(true);
    setProductSummary(null);
    setProductSummaryLoading(true);
    api().get(`/vendors/${vendor.id}/product-summary`)
      .then((r) => setProductSummary(r.data))
      .catch(() => setProductSummary(null))
      .finally(() => setProductSummaryLoading(false));
  };

  // iter36: bulk Vendor Risk Audit print
  const printVendorRiskAudit = async (vendor) => {
    try {
      const res = await api().get(`/vendors/${vendor.id}/risk-audit`);
      const d = res.data;
      const escape = (s) => String(s ?? '').replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]));
      const fmt = (s) => s ? new Date(s).toLocaleString() : '—';
      const pct = (n) => n == null ? '—' : `${n > 0 ? '+' : ''}${n}%`;
      const bandPill = (b) => `<span class="pill ${b}">${b}</span>`;
      const html = `<!doctype html><html><head><title>Vendor Risk Audit — ${escape(d.vendor.vendor_name)}</title>
        <style>
          @page { size: Letter; margin: 0.55in; }
          body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #111; line-height: 1.4; }
          h1 { font-size: 20px; margin: 0; letter-spacing: -0.02em; }
          h2 { font-size: 11px; margin: 18px 0 6px; text-transform: uppercase; letter-spacing: 0.12em; color: #475569; border-bottom: 1px solid #cbd5e1; padding-bottom: 4px; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #0f172a; padding-bottom: 10px; }
          .header .logo { max-height: 50px; max-width: 180px; object-fit: contain; }
          .meta { text-align: right; font-size: 11px; color: #666; }
          .kpis { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; margin: 14px 0; }
          .kpi { border: 1px solid #e2e8f0; padding: 8px; border-radius: 4px; background: #f8fafc; text-align: center; }
          .kpi .v { font-size: 20px; font-weight: 800; color: #0f172a; }
          .kpi .l { font-size: 8px; color: #64748b; text-transform: uppercase; letter-spacing: 0.1em; margin-top: 2px; }
          table { width: 100%; border-collapse: collapse; margin: 4px 0 12px; }
          th, td { padding: 4px 6px; border-bottom: 1px solid #e5e7eb; text-align: left; font-size: 10px; }
          th { background: #f1f5f9; font-weight: 600; color: #334155; text-transform: uppercase; letter-spacing: 0.06em; font-size: 8.5px; }
          .right { text-align: right; }
          .pill { display: inline-block; padding: 1px 6px; border-radius: 3px; font-size: 8px; text-transform: uppercase; letter-spacing: 0.06em; font-weight: 700; }
          .pill.critical { background: #fee2e2; color: #991b1b; }
          .pill.high { background: #ffedd5; color: #9a3412; }
          .pill.watch { background: #fef3c7; color: #92400e; }
          .pill.healthy { background: #d1fae5; color: #065f46; }
          .delta-up { color: #b91c1c; font-family: monospace; }
          .delta-down { color: #047857; font-family: monospace; }
          .recall-tag { display: inline-block; background: #fee2e2; color: #991b1b; font-size: 8px; padding: 0 4px; margin-left: 4px; border-radius: 2px; }
          .footer { margin-top: 22px; font-size: 9px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 6px; }
        </style></head><body>
        <div class="header">
          <div><!--LH--></div>
          <div class="meta">
            <div><strong>Vendor Risk Audit</strong></div>
            <div>${escape(d.vendor.vendor_name)}</div>
            <div>${escape(d.vendor.contact_name || '')} · ${escape(d.vendor.contact_info || '')}</div>
            <div>Generated ${fmt(d.generated_at)} by ${escape(d.generated_by)}</div>
          </div>
        </div>
        <div class="kpis">
          <div class="kpi"><div class="v">${d.totals.skus}</div><div class="l">SKUs supplied</div></div>
          <div class="kpi"><div class="v" style="color:#b91c1c">${d.totals.by_band.critical || 0}<span style="color:#94a3b8;font-size:13px;"> · ${d.totals.by_band.high || 0}H</span></div><div class="l">Critical · High</div></div>
          <div class="kpi"><div class="v" style="color:${d.totals.low_stock ? '#b91c1c' : '#16a34a'}">${d.totals.low_stock}</div><div class="l">Below reorder pt</div></div>
          <div class="kpi"><div class="v" style="color:${d.totals.in_active_recall ? '#b91c1c' : '#16a34a'}">${d.totals.in_active_recall}</div><div class="l">In active recall</div></div>
          <div class="kpi"><div class="v">${d.totals.at_risk_boms}</div><div class="l">BOMs at-risk</div></div>
        </div>
        <h2>Vendor Profile</h2>
        <table>
          <tr><th>Lead Time</th><td>${d.vendor.lead_time_days || 0}d</td><th>Payment Terms</th><td>${escape(d.vendor.payment_terms || '—')}</td></tr>
          <tr><th>Delay Risk</th><td>${d.vendor.delay_risk ?? '—'} / 5</td><th>Cost Risk</th><td>${d.vendor.cost_risk ?? '—'} / 5</td></tr>
          <tr><th>Geopolitical Risk</th><td>${d.vendor.geopolitical_risk ?? '—'} / 5</td><th>Status</th><td>${d.vendor.active_status ? 'Active' : 'Inactive'}</td></tr>
          <tr><th>Website</th><td colspan="3">${escape(d.vendor.website || '—')}</td></tr>
        </table>
        <h2>SKU Risk Sheet · ranked by severity</h2>
        ${d.rows.length === 0 ? '<div style="color:#94a3b8;font-size:10px;">No SKUs sourced from this vendor.</div>' : `
        <table>
          <tr>
            <th>SKU</th><th>Name</th><th class="right">On Hand</th><th class="right">Reorder Pt</th><th class="right">Lead</th><th class="right">Latest $</th><th class="right">Δ 90d</th><th class="right">BOMs</th><th>Risk</th>
          </tr>
          ${d.rows.map(r => `<tr>
            <td style="font-family:monospace;">${escape(r.item_sku)}${r.in_active_recall ? '<span class="recall-tag">RECALL</span>' : ''}</td>
            <td>${escape(r.item_name || '')}<br><span style="color:#94a3b8;font-size:8px;">${escape(r.category || '')}</span></td>
            <td class="right ${r.low_stock ? 'delta-up' : ''}">${r.on_hand}</td>
            <td class="right">${r.reorder_point}</td>
            <td class="right">${r.lead_time_days}d</td>
            <td class="right">${r.latest_unit_cost == null ? '—' : '$' + r.latest_unit_cost.toFixed(2)}</td>
            <td class="right ${r.cost_delta_90d_pct > 0 ? 'delta-up' : r.cost_delta_90d_pct < 0 ? 'delta-down' : ''}">${pct(r.cost_delta_90d_pct)}</td>
            <td class="right">${r.bom_count}</td>
            <td>${bandPill(r.risk_band)}${r.manual_band_override ? '<span style="font-size:7px;color:#94a3b8;display:block;">manual</span>' : ''}</td>
          </tr>${r.manual_risk_note ? `<tr><td colspan="9" style="background:#f8fafc;color:#475569;font-size:9px;padding-left:18px;border-bottom:1px solid #e5e7eb;">↳ ${escape(r.manual_risk_note)}</td></tr>` : ''}`).join('')}
        </table>`}
        ${(d.active_recalls || []).length > 0 ? `
        <h2 style="color:#991b1b;">⚠ Active Recalls Touching This Vendor's Lots</h2>
        <table><tr><th>Lot</th><th>Reason</th><th>Issued</th></tr>
          ${d.active_recalls.map(r => `<tr><td style="font-family:monospace;">${escape(r.lot)}</td><td>${escape(r.reason || '')}</td><td>${fmt(r.created_at)}</td></tr>`).join('')}
        </table>` : ''}
        <script>window.onload = () => setTimeout(() => window.print(), 400);</script>
        </body></html>`;
      const w = await openLetterheadWindow(html, { features: 'width=1000,height=1200' });
      if (!w) { toast.error('Pop-ups blocked. Allow pop-ups to print the audit.'); return; }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate risk audit');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        lead_time_days: parseInt(formData.lead_time_days) || 0,
        min_order_value: formData.min_order_value === '' || formData.min_order_value === null ? null : parseFloat(formData.min_order_value),
        delay_risk: parseInt(formData.delay_risk) || 1,
        cost_risk: parseInt(formData.cost_risk) || 1,
        geopolitical_risk: parseInt(formData.geopolitical_risk) || 1,
      };
      if (editingItem) {
        await api().put(`/vendors/${editingItem.id}`, payload);
        toast.success('Vendor updated');
      } else {
        await api().post('/vendors', payload);
        toast.success('Vendor created');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      vendor_name: item.vendor_name,
      contact_name: item.contact_name || '',
      contact_info: item.contact_info || '',
      contact_email: item.contact_email || '',
      contact_phone: item.contact_phone || '',
      website: item.website || '',
      payment_terms: item.payment_terms || '',
      gst_number: item.gst_number || '',
      default_currency: item.default_currency || 'CAD',
      min_order_value: item.min_order_value ?? '',
      ordering_notes: item.ordering_notes || '',
      preferred_order_process: item.preferred_order_process || '',
      lead_time_days: item.lead_time_days,
      active_status: item.active_status,
      star_rating: item.star_rating ?? null,
      delay_risk: item.delay_risk || 1,
      cost_risk: item.cost_risk || 1,
      geopolitical_risk: item.geopolitical_risk || 1,
      risk_notes: item.risk_notes || '',
    });
    setDialogOpen(true);
  };

  const handleArchive = async (item) => {
    if (!window.confirm(`Archive ${item.vendor_name}? History is preserved and you can unarchive later.`)) return;
    try {
      await api().delete(`/vendors/${item.id}`);
      toast.success('Vendor archived');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Archive failed');
    }
  };

  const handleUnarchive = async (item) => {
    try {
      await api().post(`/vendors/${item.id}/unarchive`);
      toast.success('Vendor unarchived');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unarchive failed');
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      vendor_name: '',
      contact_name: '',
      contact_info: '',
      contact_email: '',
      contact_phone: '',
      website: '',
      payment_terms: '',
      gst_number: '',
      default_currency: 'CAD',
      min_order_value: '',
      ordering_notes: '',
      lead_time_days: 0,
      active_status: true,
      star_rating: null,
      delay_risk: 1,
      cost_risk: 1,
      geopolitical_risk: 1,
      risk_notes: '',
    });
  };

  const filteredItems = vendors.filter(item =>
    item.vendor_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-slideIn" data-testid="vendors-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Vendors</h1>
          <p className="text-slate-500 text-sm">Manage supplier information</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-vendor-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Vendor
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Vendor Name *</Label>
                <Input
                  value={formData.vendor_name}
                  onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                  placeholder="e.g., Acme Supplies"
                  required
                  className="rounded-sm"
                  data-testid="vendor-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contact Name</Label>
                  <Input
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    placeholder="e.g., Jane Doe"
                    className="rounded-sm"
                    data-testid="vendor-contact-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Website / Vendor Link</Label>
                  <Input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://example.com"
                    className="rounded-sm mono"
                    data-testid="vendor-website-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contact Email</Label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="contact@vendor.com"
                    className="rounded-sm mono"
                    data-testid="vendor-contact-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Phone</Label>
                  <Input
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    placeholder="+1 555 555 5555"
                    className="rounded-sm mono"
                    data-testid="vendor-contact-phone-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Other Contact Info</Label>
                <Input
                  value={formData.contact_info}
                  onChange={(e) => setFormData({ ...formData, contact_info: e.target.value })}
                  placeholder="Backup email, alt phone, etc."
                  className="rounded-sm"
                  data-testid="vendor-contact-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Payment Terms</Label>
                  <Input
                    value={formData.payment_terms}
                    onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                    placeholder="e.g., Net 30"
                    className="rounded-sm"
                    data-testid="vendor-payment-terms-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>GST / HST Reg #</Label>
                  <Input value={formData.gst_number || ''} onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })} placeholder="e.g., 123456789 RT0001" className="rounded-sm mono" data-testid="vendor-gst-input" />
                </div>
                <div className="space-y-2">
                  <Label>Default Currency</Label>
                  <CurrencySelect value={formData.default_currency} onChange={(v) => setFormData({ ...formData, default_currency: v })} className="w-full" testId="vendor-currency-select" />
                </div>
                <div className="space-y-2">
                  <Label>Minimum Order Value</Label>
                  <Input type="number" min="0" step="0.01" value={formData.min_order_value ?? ''} onChange={(e) => setFormData({ ...formData, min_order_value: e.target.value })} placeholder="e.g., 500" className="rounded-sm mono" data-testid="vendor-min-order-input" />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Ordering Notes</Label>
                  <Input value={formData.ordering_notes || ''} onChange={(e) => setFormData({ ...formData, ordering_notes: e.target.value })} placeholder="e.g., $500 minimum order, free freight over $1,000" className="rounded-sm" data-testid="vendor-ordering-notes-input" />
                </div>
                <div className="space-y-2">
                  <Label>Lead Time (Days)</Label>
                  <Input
                    type="number"
                    value={formData.lead_time_days}
                    onChange={(e) => setFormData({ ...formData, lead_time_days: e.target.value })}
                    className="rounded-sm mono"
                    data-testid="vendor-lead-time-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Preferred Order Process</Label>
                <Textarea
                  value={formData.preferred_order_process}
                  onChange={(e) => setFormData({ ...formData, preferred_order_process: e.target.value })}
                  placeholder="e.g., Email PO to orders@vendor.com, then phone confirmation. EDI 850 supported. Min order $500."
                  rows={3}
                  className="rounded-sm"
                  data-testid="vendor-preferred-order-process-input"
                />
                <p className="text-[10px] text-slate-400">How Purchasing should place orders with this vendor (channel, contacts, minimums, special instructions).</p>
              </div>
              <div className="flex items-center justify-between">
                <Label>Active Status</Label>
                <Switch
                  checked={formData.active_status}
                  onCheckedChange={(checked) => setFormData({ ...formData, active_status: checked })}
                  data-testid="vendor-active-switch"
                />
              </div>
              <div className="space-y-1.5" data-testid="vendor-star-row">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Star Rating</Label>
                  <div className="flex items-center gap-0.5" data-testid="vendor-star-input">
                    {[1, 2, 3, 4, 5].map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setFormData({ ...formData, star_rating: formData.star_rating === n ? null : n })}
                        data-testid={`vendor-form-star-${n}`}
                      >
                        <Star className={`w-5 h-5 ${(formData.star_rating || 0) >= n ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} />
                      </button>
                    ))}
                    {formData.star_rating && (
                      <button type="button" onClick={() => setFormData({ ...formData, star_rating: null })} className="text-[10px] uppercase text-slate-400 ml-1 hover:text-slate-700">clear</button>
                    )}
                  </div>
                </div>
                <p className="text-[10px] text-slate-400">Quick-reference 1–5 quality / preference rating.</p>
              </div>

              <div className="border-t border-slate-200 pt-4 space-y-4" data-testid="vendor-risk-section">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Global Risk Index</div>
                {[
                  { key: 'delay_risk', label: 'Shipping / Delay Risk', help: 'Likelihood of late or disrupted shipments' },
                  { key: 'cost_risk', label: 'Cost Volatility', help: 'How much unit & shipping costs swing' },
                  { key: 'geopolitical_risk', label: 'Geopolitical / Trade Risk', help: 'Tariffs, sanctions, regional instability' },
                ].map(({ key, label, help }) => (
                  <div key={key} className="space-y-1.5" data-testid={`vendor-${key.replace('_', '-')}-row`}>
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">{label}</Label>
                      <span className="text-xs mono text-slate-700 px-2 py-0.5 bg-slate-100 rounded-sm">
                        {formData[key]} / 5
                      </span>
                    </div>
                    <input
                      type="range"
                      min={1}
                      max={5}
                      step={1}
                      value={formData[key]}
                      onChange={(e) => setFormData({ ...formData, [key]: parseInt(e.target.value) })}
                      className="w-full accent-orange-500"
                      data-testid={`vendor-${key.replace('_', '-')}-slider`}
                    />
                    <div className="flex justify-between text-[10px] text-slate-400 uppercase tracking-wider">
                      <span>Low</span><span>{help}</span><span>Severe</span>
                    </div>
                  </div>
                ))}
                <div className="space-y-2">
                  <Label className="text-sm">Risk Notes</Label>
                  <Textarea
                    value={formData.risk_notes}
                    onChange={(e) => setFormData({ ...formData, risk_notes: e.target.value })}
                    placeholder="e.g. Sole-source from one factory in Shenzhen; tariff review pending Q3"
                    className="rounded-sm"
                    rows={2}
                    data-testid="vendor-risk-notes"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="vendor-submit-btn">
                  {editingItem ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="relative max-w-sm flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search vendors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 rounded-sm"
                data-testid="vendor-search-input"
              />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="vendor-archived-toggle">
              <Switch checked={showArchived} onCheckedChange={setShowArchived} />
              <span className="text-slate-600">Show archived</span>
            </label>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="spinner" />
            </div>
          ) : filteredItems.length > 0 ? (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor Name</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Lead Time</TableHead>
                  <TableHead className="text-right">Items</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map((item) => {
                  const itemCount = items.filter(i => i.preferred_vendor_id === item.id).length;
                  const lowCount = items.filter(i => i.preferred_vendor_id === item.id && (i.current_quantity || 0) <= (i.reorder_point || 0) && (i.reorder_point || 0) > 0).length;
                  return (
                    <TableRow key={item.id} data-testid={`vendor-row-${item.id}`} className={item.archived ? 'opacity-60' : ''}>
                      <TableCell>
                        <button
                          onClick={() => navigate(`/vendors/view/${item.id}`)}
                          className="font-medium text-left hover:text-orange-600 cursor-pointer underline-offset-2 hover:underline"
                          data-testid={`vendor-name-link-${item.id}`}
                        >
                          {item.vendor_name}
                          {item.risk_review_required && (
                            <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-sm" data-testid={`vendor-review-pending-${item.id}`}>Review Pending</span>
                          )}
                        </button>
                      </TableCell>
                      <TableCell data-testid={`vendor-row-rating-${item.id}`}>
                        {item.star_rating ? (
                          <div className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map(n => (
                              <Star key={n} className={`w-3 h-3 ${item.star_rating >= n ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                            ))}
                          </div>
                        ) : <span className="text-xs text-slate-300">—</span>}
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm">
                        {item.contact_name ? (
                          <div data-testid={`vendor-row-contact-${item.id}`}>
                            <div className="font-medium text-slate-700">{item.contact_name}</div>
                            <div className="text-xs text-slate-400">{item.contact_phone || item.contact_info || ''}</div>
                          </div>
                        ) : (
                          <span className="text-slate-500">{item.contact_info || '—'}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm" data-testid={`vendor-row-email-${item.id}`}>
                        {item.contact_email ? (
                          <a href={`mailto:${item.contact_email}`} className="text-orange-600 hover:underline mono break-all" onClick={(e) => e.stopPropagation()}>
                            {item.contact_email.length > 24 ? item.contact_email.slice(0, 24) + '…' : item.contact_email}
                          </a>
                        ) : <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell className="mono">{item.lead_time_days} days</TableCell>
                      <TableCell className="text-right text-sm" data-testid={`vendor-row-items-${item.id}`}>
                        <span className="font-medium text-slate-700">{itemCount}</span>
                        {lowCount > 0 && (
                          <span className="ml-1 text-xs text-red-600">· {lowCount} low</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={`status-badge ${item.archived ? 'status-obsolete' : item.active_status ? 'status-released' : 'status-obsolete'}`}>
                          {item.archived ? 'Archived' : item.active_status ? 'Active' : 'Inactive'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/vendors/view/${item.id}`)} title="View Profile" data-testid={`vendor-view-${item.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => printVendorRiskAudit(item)} title="Print Risk Audit" data-testid={`vendor-risk-audit-${item.id}`}>
                            <ShieldAlert className="w-4 h-4 text-orange-600" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(item)} data-testid={`edit-vendor-${item.id}`}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          {item.archived ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUnarchive(item)}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              data-testid={`unarchive-vendor-${item.id}`}
                              title="Unarchive"
                            >
                              <ArchiveRestore className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleArchive(item)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`archive-vendor-${item.id}`}
                              title="Archive"
                            >
                              <Archive className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center h-48 text-slate-500">
              <Truck className="w-12 h-12 mb-3 text-slate-300" />
              <p>No vendors found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Vendor Card Dialog (CRM Style) */}
      <Dialog open={vendorCardOpen} onOpenChange={setVendorCardOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedVendor && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                    <Truck className="w-6 h-6 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xl">{selectedVendor.vendor_name}</p>
                    <p className="text-sm font-normal text-slate-500">Vendor Profile</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-sm"
                    onClick={() => printVendorRiskAudit(selectedVendor)}
                    data-testid="vendor-card-risk-audit-btn"
                  >
                    <Printer className="w-4 h-4 mr-1" /> Risk Audit
                  </Button>
                </DialogTitle>
              </DialogHeader>

              {/* Vendor Info Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 my-4">
                <Card data-testid="vendor-card-contact-name">
                  <CardContent className="p-4 flex items-center gap-3">
                    <Mail className="w-5 h-5 text-slate-400" />
                    <div className="min-w-0">
                      <p className="text-xs text-slate-500">Contact</p>
                      <p className="text-sm font-medium truncate">{selectedVendor.contact_name || 'N/A'}</p>
                      {selectedVendor.contact_info && (
                        <p className="text-xs text-slate-400 truncate">{selectedVendor.contact_info}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
                <Card data-testid="vendor-card-website">
                  <CardContent className="p-4 flex items-center gap-3">
                    <Phone className="w-5 h-5 text-slate-400" />
                    <div className="min-w-0">
                      <p className="text-xs text-slate-500">Website</p>
                      {selectedVendor.website ? (
                        <a
                          href={selectedVendor.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-orange-600 hover:underline truncate block"
                        >
                          {selectedVendor.website.replace(/^https?:\/\//, '').slice(0, 24)}
                        </a>
                      ) : <p className="text-sm text-slate-400">N/A</p>}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 flex items-center gap-3">
                    <DollarSign className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Payment Terms</p>
                      <p className="text-sm font-medium">{selectedVendor.payment_terms || 'N/A'}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 flex items-center gap-3">
                    <Clock className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Lead Time</p>
                      <p className="text-sm font-medium">{selectedVendor.lead_time_days} days</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 flex items-center gap-3">
                    <Package className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Items Supplied</p>
                      <p className="text-sm font-medium">{vendorItems.length} items</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Tabs defaultValue="catalog">
                <TabsList>
                  <TabsTrigger value="catalog" data-testid="vendor-tab-catalog">Product Catalog</TabsTrigger>
                  <TabsTrigger value="reorder" data-testid="vendor-tab-reorder">Reorder Summary</TabsTrigger>
                  <TabsTrigger value="alerts">Stock Alerts</TabsTrigger>
                  <TabsTrigger value="orders">Request Order</TabsTrigger>
                </TabsList>

                <TabsContent value="catalog" className="mt-4 space-y-4">
                  {productSummaryLoading && (
                    <div className="text-center py-6 text-sm text-slate-400">Loading catalog…</div>
                  )}
                  {!productSummaryLoading && productSummary && (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="vendor-catalog-totals">
                        <div className="rounded-sm border border-slate-200 p-3">
                          <div className="text-xs uppercase tracking-wider text-slate-500">SKUs</div>
                          <div className="text-2xl font-bold text-slate-900 mono">{productSummary.totals.skus}</div>
                        </div>
                        <div className="rounded-sm border border-slate-200 p-3">
                          <div className="text-xs uppercase tracking-wider text-slate-500">On Hand (units)</div>
                          <div className="text-2xl font-bold text-slate-900 mono">{productSummary.totals.total_on_hand}</div>
                        </div>
                        <div className="rounded-sm border border-slate-200 p-3">
                          <div className="text-xs uppercase tracking-wider text-slate-500">Categories</div>
                          <div className="text-2xl font-bold text-slate-900 mono">{productSummary.totals.categories}</div>
                        </div>
                        <div className="rounded-sm border border-slate-200 p-3">
                          <div className="text-xs uppercase tracking-wider text-slate-500">Low Stock</div>
                          <div className={`text-2xl font-bold mono ${productSummary.totals.low_stock_count > 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                            {productSummary.totals.low_stock_count}
                          </div>
                        </div>
                      </div>
                      {productSummary.by_category.length > 0 && (
                        <div className="flex flex-wrap gap-2 text-xs">
                          {productSummary.by_category.map((c) => (
                            <span key={c.category} className="px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-sm text-slate-700">
                              {c.category}: <span className="font-medium">{c.count}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {productSummary && productSummary.catalog.length > 0 ? (
                    <Table className="data-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>SKU</TableHead>
                          <TableHead>Item Name</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead className="text-right">On Hand</TableHead>
                          <TableHead className="text-right">Reorder Pt</TableHead>
                          <TableHead className="text-right">Latest $</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {productSummary.catalog.map(item => (
                          <TableRow key={item.id} className={item.low_stock ? 'bg-red-50' : ''} data-testid={`vendor-catalog-row-${item.id}`}>
                            <TableCell className="mono">{item.item_sku}</TableCell>
                            <TableCell>{item.item_name}</TableCell>
                            <TableCell className="text-slate-500 text-sm">{item.item_category}</TableCell>
                            <TableCell className="text-right mono">{item.current_quantity}</TableCell>
                            <TableCell className="text-right mono">{item.reorder_point}</TableCell>
                            <TableCell className="text-right mono text-slate-600">{item.latest_unit_cost != null ? `$${item.latest_unit_cost}` : '—'}</TableCell>
                            <TableCell>
                              {item.low_stock ? (
                                <span className="flex items-center gap-1 text-red-600 text-sm">
                                  <AlertTriangle className="w-4 h-4" /> Low Stock
                                </span>
                              ) : (
                                <span className="text-green-600 text-sm">In Stock</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (!productSummaryLoading && (
                    <div className="text-center py-8 text-slate-500">
                      <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No items from this vendor</p>
                    </div>
                  ))}
                </TabsContent>

                <TabsContent value="reorder" className="mt-4 space-y-4" data-testid="vendor-reorder-tab-content">
                  {productSummaryLoading && (
                    <div className="text-center py-6 text-sm text-slate-400">Loading…</div>
                  )}
                  {!productSummaryLoading && productSummary && (
                    productSummary.reorder.length > 0 ? (
                      <>
                        <div className="grid grid-cols-3 gap-3" data-testid="vendor-reorder-totals">
                          <div className="rounded-sm border border-red-200 bg-red-50 p-3">
                            <div className="text-xs uppercase tracking-wider text-red-700">Items to Reorder</div>
                            <div className="text-2xl font-bold text-red-700 mono">{productSummary.totals.low_stock_count}</div>
                          </div>
                          <div className="rounded-sm border border-slate-200 p-3">
                            <div className="text-xs uppercase tracking-wider text-slate-500">Suggested Units</div>
                            <div className="text-2xl font-bold text-slate-900 mono">{productSummary.totals.reorder_units}</div>
                          </div>
                          <div className="rounded-sm border border-slate-200 p-3">
                            <div className="text-xs uppercase tracking-wider text-slate-500">Est. Spend</div>
                            <div className="text-2xl font-bold text-slate-900 mono">${productSummary.totals.estimated_reorder_spend}</div>
                          </div>
                        </div>
                        <div className="flex justify-end">
                          <Button
                            className="bg-orange-500 hover:bg-orange-600"
                            onClick={() => {
                              setVendorCardOpen(false);
                              setPurchaserContext({ type: 'vendor_order', item: null, vendor: selectedVendor });
                              setPurchaserDialogOpen(true);
                            }}
                            data-testid="vendor-reorder-email-purchaser-btn"
                          >
                            <Mail className="w-4 h-4 mr-2" /> Email Purchaser for Reorder
                          </Button>
                        </div>
                        <Table className="data-table">
                          <TableHeader>
                            <TableRow>
                              <TableHead>SKU</TableHead>
                              <TableHead>Item</TableHead>
                              <TableHead className="text-right">On Hand</TableHead>
                              <TableHead className="text-right">Reorder Pt</TableHead>
                              <TableHead className="text-right">Suggested Qty</TableHead>
                              <TableHead className="text-right">Est. Cost</TableHead>
                              <TableHead className="text-right">Lead</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {productSummary.reorder.map((row) => (
                              <TableRow key={row.id} data-testid={`vendor-reorder-row-${row.id}`}>
                                <TableCell className="mono">{row.item_sku}</TableCell>
                                <TableCell>{row.item_name}</TableCell>
                                <TableCell className="text-right mono text-red-700">{row.current_quantity}</TableCell>
                                <TableCell className="text-right mono">{row.reorder_point}</TableCell>
                                <TableCell className="text-right mono font-semibold">{row.suggested_qty}</TableCell>
                                <TableCell className="text-right mono">{row.estimated_line_cost != null ? `$${row.estimated_line_cost.toFixed(2)}` : '—'}</TableCell>
                                <TableCell className="text-right mono text-slate-500">{row.lead_time_days}d</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </>
                    ) : (
                      <div className="text-center py-8 text-emerald-700" data-testid="vendor-reorder-empty">
                        <Package className="w-12 h-12 mx-auto mb-3" />
                        <p className="font-medium">No items below reorder point</p>
                        <p className="text-sm text-slate-500">Inventory from this vendor is healthy.</p>
                      </div>
                    )
                  )}
                </TabsContent>

                <TabsContent value="alerts" className="mt-4">
                  {vendorItems.filter(i => i.current_quantity <= (i.reorder_point || 0)).length > 0 ? (
                    <div className="space-y-3">
                      {vendorItems.filter(i => i.current_quantity <= (i.reorder_point || 0)).map(item => (
                        <Card key={item.id} className="border-red-200 bg-red-50">
                          <CardContent className="p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <AlertTriangle className="w-5 h-5 text-red-500" />
                              <div>
                                <p className="font-medium">{item.name}</p>
                                <p className="text-sm text-slate-500">SKU: {item.sku}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-red-600 font-bold">{item.current_quantity} on hand</p>
                              <p className="text-sm text-slate-500">Reorder at: {item.reorder_point || 0}</p>
                            </div>
                            <Button 
                              size="sm" 
                              className="bg-orange-500 hover:bg-orange-600"
                              onClick={() => {
                                setPurchaserContext({ type: 'low_stock', item, vendor: selectedVendor });
                                setPurchaserDialogOpen(true);
                              }}
                              data-testid={`vendor-low-stock-email-${item.id}`}
                            >
                              <Mail className="w-4 h-4 mr-1" />
                              Email Purchaser
                            </Button>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-green-600">
                      <Package className="w-12 h-12 mx-auto mb-3" />
                      <p className="font-medium">All items in stock!</p>
                      <p className="text-sm text-slate-500">No low stock alerts for this vendor</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="orders" className="mt-4">
                  <Card>
                    <CardContent className="p-6 text-center">
                      <Mail className="w-12 h-12 mx-auto mb-4 text-orange-500" />
                      <h3 className="font-semibold text-lg mb-2">Request a Purchase</h3>
                      <p className="text-slate-500 mb-4">
                        Email the purchasing team to start a new order from {selectedVendor.vendor_name}
                      </p>
                      <Button 
                        className="bg-orange-500 hover:bg-orange-600"
                        onClick={() => {
                          setVendorCardOpen(false);
                          setPurchaserContext({ type: 'vendor_order', item: null, vendor: selectedVendor });
                          setPurchaserDialogOpen(true);
                        }}
                        data-testid="vendor-email-purchaser-btn"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Email Purchaser
                      </Button>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      <EmailPurchaserDialog
        open={purchaserDialogOpen}
        onOpenChange={setPurchaserDialogOpen}
        requestType={purchaserContext.type}
        item={purchaserContext.item}
        vendor={purchaserContext.vendor}
      />
    </div>
  );
};

export default VendorsPage;
